import React, { useEffect, useRef, useState } from 'react';
import { I18n } from 'react-redux-i18n';
import { Button, Grid, Icon, Item, Loader } from 'semantic-ui-react';

import { useAppSelector } from '../../../hooks/redux/useApp';
import { TemplateHeader } from '../../../components/template/templateHeader';
import { TemplateHorizontalRule } from '../../../components/template/templateHorizontalRule';
import * as templateHelper from '../../../components/template/templateHelper';
import { updateCachedGiftAidStatus } from '../../../helpers/orgInfoCache';
import swiftaidLogo from '../../../images/swiftaidLogo.svg';
import { refreshSwiftaidLink, startSwiftaidLink } from './api';
import {
  ConnectPhase,
  SwiftaidLinkState,
  connectPhase,
  giftAidStatusPath,
  readGiftAidStatus,
  swiftaidLinkState,
  swiftaidLinkStateI18nKey,
  swiftaidLinkStateIcon,
  hmrcCharityReferencePath
} from './constants';
import styles from './swiftaidConnection.module.css';

const i18nPath = 'template.organisation.content.finance.swiftaid';

/**
 * The organisation completes the Swiftaid form in this tab. The form used to open
 * in a new tab, but browsers treat a `window.open` issued after two awaited API
 * calls as a popup and block it, so the button navigates the current route
 * instead. The link state is re-read from the organisation record when the user
 * comes back, so there is nothing to poll for while they are away.
 */

export const SwiftaidConnection = () => {
  const organizationId = useAppSelector((state: any) =>
    templateHelper.getValue(state.templateBuilderEx, 'id')
  );

  /**
   * The organisation record already carries the link state, so the panel can
   * report it on arrival without calling the refresh endpoint. `checkedState`
   * holds the result of an explicit check and takes over once there is one,
   * since the record in the template state is not re-read.
   */
  const recordState = useAppSelector((state: any) =>
    readGiftAidStatus(
      templateHelper.getValue(state.templateBuilderEx, giftAidStatusPath)
    )
  );
  const [checkedState, setCheckedState] = useState<SwiftaidLinkState | null>(
    null
  );
  const linkState = checkedState ?? recordState;
  const hmrcCharityReference = useAppSelector((state: any) =>
    templateHelper.getValue(state.templateBuilderEx, hmrcCharityReferencePath)
  );
  const [phase, setPhase] = useState<ConnectPhase>(connectPhase.idle);
  const [error, setError] = useState<string | null>(null);

  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Re-read the link state and record it. Throws when the refresh call fails so
   * the connect button can surface it.
   *
   * Deliberately not called on mount. Opening or navigating back to the finance
   * tab must not hit the refresh endpoint, so this only runs when the user
   * presses connect.
   */
  const readLinkState = async (): Promise<SwiftaidLinkState> => {
    const status = await refreshSwiftaidLink(organizationId);
    // The Gift Aid nudge in the layout reads the cached status, so a fresh
    // answer is recorded there too and the nudge goes away as soon as the link
    // exists. Not tied to the panel still being mounted: the cache is not ours.
    updateCachedGiftAidStatus(organizationId, status);
    if (isMountedRef.current) {
      setCheckedState(status);
    }
    return status;
  };

  /**
   * One button, two steps. The link state on the record can be stale (the form
   * may have been completed in another session), so the button first asks
   * Swiftaid whether the organisation is already linked. Only when it is not
   * does it start a link and send the user to the registration form. An
   * organisation the check finds ineligible is left where it is: there is no
   * form for it to fill in. A linked organisation keeps the button too, so the
   * check can be repeated at any time without the panel having to be reloaded.
   */
  const onConnectClick = async () => {
    if (!organizationId) {
      return;
    }
    setError(null);
    setPhase(connectPhase.checking);

    let status: SwiftaidLinkState;
    try {
      status = await readLinkState();
    } catch (e) {
      if (isMountedRef.current) {
        setError(I18n.t(`${i18nPath}.status-error`));
        setPhase(connectPhase.idle);
      }
      return;
    }
    if (!isMountedRef.current) {
      return;
    }
    if (
      status === swiftaidLinkState.linked ||
      status === swiftaidLinkState.notEligible
    ) {
      setPhase(connectPhase.idle);
      return;
    }

    setPhase(connectPhase.starting);
    let url: string | null;
    try {
      url = await startSwiftaidLink(organizationId);
    } catch (e) {
      url = null;
    }
    if (!isMountedRef.current) {
      return;
    }
    if (!url) {
      setError(I18n.t(`${i18nPath}.start-error`));
      setPhase(connectPhase.idle);
      return;
    }
    // Same tab on purpose: a `window.open` this far from the click is blocked as
    // a popup. The button stays in its loading state while the browser leaves.
    window.location.assign(url);
  };

  const isConnected = linkState === swiftaidLinkState.linked;
  // An ineligible organisation cannot link at all, so the action is not offered.
  const isEligible = linkState !== swiftaidLinkState.notEligible;
  // The panel is only shown for UK organisations, and the API treats one as
  // ineligible when its HMRC charity reference is blank, so say how to fix it.
  // Read from the live form value: once one is typed in, the hint gives way to
  // the plain status until the saved record says otherwise.
  const isMissingHmrcReference =
    !isEligible &&
    !(typeof hmrcCharityReference === 'string' && hmrcCharityReference.trim());
  const isChecking = phase === connectPhase.checking;
  const isBusy = phase !== connectPhase.idle;
  const statusKey = swiftaidLinkStateI18nKey[linkState];
  // A linked organisation keeps the button so the link can be re-checked on
  // demand; the label changes to say that is what it now does.
  const buttonKey = isConnected ? 'check-button' : 'connect-button';

  return (
    <React.Fragment>
      {/*
       * TemplateHorizontalRule carries the `template-divider` class, whose
       * negative side margins cancel the template container's padding so the
       * rule reaches both edges of the panel. A plain `hr` stops at the padding.
       */}
      <TemplateHorizontalRule />
      <section
        className={styles.section}
        aria-labelledby="swiftaidSectionHeader"
        data-testid="swiftaid-section"
      >
        {/*
         * Same component, tag and i18n wiring as the "Payment gateways" header
         * above, so the two headings match in font, size and spacing.
         */}
        <TemplateHeader
          as="h3"
          id="swiftaidSectionHeader"
          label={`${i18nPath}.section-header`}
        />
        {/*
         * The same inline margin the Stripe panel uses, so the gap below the
         * heading matches. It has to be inline: `.ui.grid` sets its own margins
         * at a higher specificity than a CSS module class.
         */}
        <Grid style={{ marginTop: 12 }}>
          <Grid.Row>
            <Grid.Column width={11}>
              <Item.Group>
                <Item>
                  {/*
                   * Same slot and size as the Stripe and Salesforce panels. The
                   * heading beside it already names Swiftaid, so the logo is
                   * decorative and gets an empty alt rather than repeating it.
                   */}
                  <Item.Image
                    size="tiny"
                    src={swiftaidLogo}
                    alt=""
                    className={styles.logo}
                  />
                  <Item.Content>
                    <Item.Header>{I18n.t(`${i18nPath}.title`)}</Item.Header>
                    <Item.Description className={styles.description}>
                      {I18n.t(`${i18nPath}.description`)}
                    </Item.Description>
                    {isChecking ? (
                      <div className={styles.status} role="status">
                        <Loader active inline size="tiny" />
                        <span>{I18n.t(`${i18nPath}.checking`)}</span>
                      </div>
                    ) : (
                      <div
                        className={`${styles.status} ${
                          isConnected ? styles.connected : styles.disconnected
                        }`}
                        role="status"
                        data-link-state={linkState}
                      >
                        <Icon
                          aria-hidden="true"
                          name={swiftaidLinkStateIcon[linkState]}
                        />
                        <span>{I18n.t(`${i18nPath}.${statusKey}`)}</span>
                      </div>
                    )}
                    {!isChecking && isMissingHmrcReference && (
                      <p
                        className={styles.reason}
                        data-testid="swiftaid-not-eligible-reason"
                      >
                        {I18n.t(`${i18nPath}.not-eligible-reason`)}
                      </p>
                    )}
                    {error && (
                      <div className={styles.error} role="alert">
                        {error}
                      </div>
                    )}
                  </Item.Content>
                </Item>
              </Item.Group>
            </Grid.Column>
            <Grid.Column width={5}>
              {isEligible && (
                <div className={styles.actions}>
                  <Button
                    id="btnSwiftaidConnect"
                    onClick={onConnectClick}
                    loading={isBusy}
                    disabled={isBusy || !organizationId}
                  >
                    {I18n.t(`${i18nPath}.${buttonKey}`)}
                  </Button>
                </div>
              )}
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </section>
    </React.Fragment>
  );
};

export default SwiftaidConnection;
